import { Box, CircularProgress } from '@mui/material';

import { useInterfaceLanguage } from '@/shared/i18n';
import { formatInterfaceNumber, getIntlLocale } from '@/shared/i18n/formatLocale';
import { isBusiness250Plan } from '@/shared/lib/billing/isBusiness250Plan';
import { PaymentInterval, Plan, PlanCategory } from '@/shared/types/Billing';

import { getPlanCardPricing } from '../../../lib/utils/getPlanCardPricing';
import { prettifyPlanName } from '../../../lib/utils/getPlanName';
import { PAYWALL_PLAN_COPY } from '../lib/constants';
import { SCard, SCta, SSavingsBadge } from '../styled';
import { PaywallFeatureList, PaywallMinutes } from './PaywallFeatures';

type PaywallPlanCardProps = {
  isCompact: boolean;
  isLoading: boolean;
  isPurchaseInProgress: boolean;
  period: PaymentInterval;
  plan: Plan;
  onContactSales: (plan: Plan) => void;
  onGetStarted: (plan: Plan, priceId: string) => void;
};

export const PaywallPlanCard = ({
  isCompact,
  isLoading,
  isPurchaseInProgress,
  period,
  plan,
  onContactSales,
  onGetStarted,
}: PaywallPlanCardProps) => {
  const { locale, t } = useInterfaceLanguage();
  const copy = PAYWALL_PLAN_COPY[plan.category];
  const pricing = getPlanCardPricing(plan, period);

  const isHighlighted = plan.category === PlanCategory.CreatorPro;
  const isYearly = period === PaymentInterval.Year;
  const isContactSales = plan.category === PlanCategory.Business && !isBusiness250Plan(plan);
  const accentColor = isHighlighted ? 'primary.600' : 'grey.900';

  const currencyFormat = new Intl.NumberFormat(getIntlLocale(locale), {
    currency: pricing.currency.toUpperCase(),
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
    style: 'currency',
  });
  const formatCents = (cents: number) => currencyFormat.format(cents / 100);
  const formatWhole = (amount: number) => currencyFormat.format(amount);

  const savingsLabel =
    isYearly && pricing.yearlySavingsPercent > 0
      ? t('pricing.savings.year', {
          amount: formatCents(pricing.yearlySavingsCents),
          percent: pricing.yearlySavingsPercent,
        })
      : null;
  const billedLabel =
    isYearly && pricing.annualTotalCents !== undefined
      ? t('pricing.price.billedYearly', { amount: formatCents(pricing.annualTotalCents) })
      : t('pricing.price.billedMonthly');

  const handleClick = () => {
    if (isContactSales) {
      onContactSales(plan);
      return;
    }

    if (pricing.price) onGetStarted(plan, pricing.price.id);
  };

  return (
    <SCard
      $isCompact={isCompact}
      $isHighlighted={isHighlighted}
      data-testid={`trial-paywall-experiment-plan-${plan.category}`}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, paddingBottom: isCompact ? 14 : 16 }}>
        <Box
          sx={{
            color: accentColor,
            fontSize: isCompact ? 20 : 22,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            lineHeight: isCompact ? '26px' : '28px',
          }}
          component="span"
        >
          {prettifyPlanName(plan.name).trim()}
        </Box>
        {copy && (
          <Box component="span" sx={{ color: 'grey.600', fontSize: 13, lineHeight: '17px', textWrap: 'pretty' }}>
            {t(copy.descriptionKey)}
          </Box>
        )}
      </Box>
      {!isCompact && <Box />}
      {savingsLabel && <SSavingsBadge>{savingsLabel}</SSavingsBadge>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ alignItems: 'baseline', columnGap: 6, display: 'flex', flexWrap: 'wrap' }}>
          {isYearly && pricing.strikePerMonth !== undefined && (
            <Box
              sx={{
                color: 'grey.400',
                textDecoration: 'line-through',
                ...(isCompact ? { typography: 'title-4-sb' } : { fontSize: 20, fontWeight: 600, lineHeight: '24px' }),
              }}
              component="span"
            >
              {formatWhole(pricing.strikePerMonth)}
            </Box>
          )}
          <Box
            sx={{
              color: accentColor,
              fontSize: isCompact ? 32 : 36,
              fontWeight: 700,
              letterSpacing: '-0.02em',
              lineHeight: isCompact ? '38px' : '42px',
            }}
            component="span"
          >
            {formatWhole(pricing.perMonth)}
          </Box>
          <Box component="span" sx={{ color: 'grey.500', typography: 'body-2' }}>
            {t('pricing.price.perMonth')}
          </Box>
        </Box>
        <Box component="span" sx={{ color: 'grey.500', fontSize: 13, lineHeight: '18px' }}>
          {billedLabel}
        </Box>
      </Box>
      <Box sx={{ paddingTop: isCompact ? 14 : 16 }}>
        <PaywallMinutes
          label={t('pricing.minutes.included', { count: formatInterfaceNumber(locale, pricing.minutes) })}
        />
      </Box>
      <PaywallFeatureList featureKeys={copy?.featureKeys ?? []} isCompact={isCompact} />
      <SCta
        $isCompact={isCompact}
        $variant={isHighlighted ? 'primary' : 'secondary'}
        data-testid={`trial-paywall-experiment-cta-${plan.category}`}
        disabled={isPurchaseInProgress}
        onClick={handleClick}
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={16} />
        ) : (
          t(isContactSales ? 'pricing.actions.contactUs' : 'pricing.actions.getStarted')
        )}
      </SCta>
    </SCard>
  );
};
